import { DeadlineExceededError } from "#deadline-exceeded-error";
import { notifyDeadlineExceeded } from "#deadline-listeners";
import { type Deadline, resolveDeadlineMs } from "#deadline-ms";

export type BoundedPromiseExecutor<T> = (
  resolve: (value: T | PromiseLike<T>) => void,
  reject: (reason?: unknown) => void,
  signal: AbortSignal,
  /** Restart the stall deadline when a long operation makes progress. */
  reportProgress: () => void,
) => (() => void) | void;

export type BoundedPromiseOptions = {
  deadline: Deadline;
  /**
   * What happens when the deadline passes. `reject` (the default) hands the awaiter a
   * `DeadlineExceededError`. `resolve` is for HOLDS — a gate or pause that must fail open — and
   * settles with `undefined`, so it is only available for `void` promises.
   */
  onDeadline?: "reject" | "resolve";
  /** Names the wait in the error and in hang reports: what is being waited FOR. */
  operation: string;
};

/**
 * The one sanctioned `new Promise`: a hand-rolled promise that cannot outlive its deadline.
 *
 * A promise whose resolver is handed to an event, a callback, a ref or another module settles only
 * if that outside party keeps its word — a listener removed early, an event that never fires, a
 * component that unmounts, an exception thrown before `resolve` all leave it pending FOREVER, and
 * everything awaiting it spins forever too. Here the timer is armed before the executor runs, so
 * every path ends: the executor's outcome, or the deadline.
 *
 * The executor may return a cleanup function; it runs exactly once when the promise settles by
 * any route (remove listeners, clear native timers). `signal` aborts at the deadline so the
 * executor can stop work nobody will observe.
 */
export function boundedPromise(
  options: BoundedPromiseOptions & { onDeadline: "resolve" },
  executor: BoundedPromiseExecutor<void>,
): Promise<void>;
export function boundedPromise<T>(
  options: BoundedPromiseOptions & { onDeadline?: "reject" },
  executor: BoundedPromiseExecutor<T>,
): Promise<T>;
export function boundedPromise<T>(
  { deadline, onDeadline = "reject", operation }: BoundedPromiseOptions,
  executor: BoundedPromiseExecutor<T>,
): Promise<T> {
  const deadlineMs = resolveDeadlineMs(operation, deadline);
  const controller = new AbortController();
  // oxlint-disable-next-line han-/no-unbounded-promise -- the bounded primitive itself.
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    let cleanup: (() => void) | void;
    const settle = (): boolean => {
      if (settled) {
        return false;
      }
      settled = true;
      if (timer !== undefined) {
        clearTimeout(timer);
      }
      try {
        cleanup?.();
      } catch {
        // A failed cleanup cannot change how the wait ended.
      }
      return true;
    };
    const expire = () => {
      if (deadlineMs === null) {
        return;
      }
      if (!settle()) {
        return;
      }
      const error = new DeadlineExceededError(operation, deadlineMs);
      controller.abort(error);
      notifyDeadlineExceeded(error);
      if (onDeadline === "resolve") {
        // Only reachable for holds, which are `void` by contract (see the overloads).
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- T is void here
        resolve(undefined as T);
      } else {
        reject(error);
      }
    };
    let timer = deadlineMs === null ? undefined : setTimeout(expire, deadlineMs);
    try {
      cleanup = executor(
        (value) => {
          if (settle()) {
            resolve(value);
          }
        },
        (reason) => {
          if (settle()) {
            reject(reason);
          }
        },
        controller.signal,
        () => {
          if (settled || deadlineMs === null) {
            return;
          }
          if (timer !== undefined) {
            clearTimeout(timer);
          }
          timer = setTimeout(expire, deadlineMs);
        },
      );
      if (settled) {
        // Settled synchronously inside the executor, before its cleanup was known.
        cleanup?.();
      }
    } catch (error) {
      if (settle()) {
        reject(error);
      }
    }
  });
}
